import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Sun, Moon } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
    { name: 'FAQ', path: '/faq' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[var(--nav-bg)] backdrop-blur-sm border-b border-[var(--border)]" data-testid="navbar">
        <div className="container-main">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center group" data-testid="logo-link">
              <img
                src={theme === 'dark' ? '/images/ByteConLogoDark.png' : '/images/ByteConLogo.png'}
                alt="Byt3con Logo"
                className="h-16 w-auto object-contain"
                loading="eager"
                fetchPriority="high"
                decoding="async"
                height="64"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-10">
              {navLinks.map((link) =>
                link.external ? (
                  <a
                    key={link.href}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="nav-link font-mono text-sm uppercase tracking-wider hover:text-[var(--primary)] transition-colors duration-200"
                    data-testid={`nav-link-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                  </a>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`nav-link font-mono text-sm uppercase tracking-wider ${
                      isActive(link.path) ? 'active' : ''
                    }`}
                    data-testid={`nav-link-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                  </Link>
                )
              )}
            </div>

            {/* Theme Toggle & CTA Button */}
            <div className="hidden md:flex items-center gap-4">
              {/* Theme Toggle Button */}
              <button
                onClick={toggleTheme}
                className="p-2.5 rounded-lg border border-[var(--border)] bg-[var(--surface)] hover:bg-[var(--surface-highlight)] hover:border-[var(--primary)] transition-all duration-200"
                data-testid="theme-toggle-desktop"
                aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
              >
                {theme === 'dark' ? (
                  <Sun className="w-5 h-5 text-[var(--text-muted)] hover:text-[var(--primary)]" />
                ) : (
                  <Moon className="w-5 h-5 text-[var(--text-muted)] hover:text-[var(--primary)]" />
                )}
              </button>

              {/* <Link
                to="/contact"
                className="bg-[var(--primary)] text-black font-mono font-bold px-6 py-3 text-sm uppercase tracking-widest hover:bg-[var(--primary-dim)] transition-colors duration-200 shadow-[0_0_15px_var(--glow-primary)] hover:shadow-[0_0_25px_var(--glow-primary)]"
                data-testid="cta-get-started"
              >
                Get Started
              </Link> */}
            </div>

            {/* Mobile Menu Button */}
            <div className="flex md:hidden items-center gap-2">
              {/* Mobile Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg border border-[var(--border)] bg-[var(--surface)] text-[var(--text-muted)] hover:text-[var(--primary)] transition-colors duration-200"
                data-testid="theme-toggle-mobile"
                aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
              >
                {theme === 'dark' ? (
                  <Sun className="w-5 h-5" />
                ) : (
                  <Moon className="w-5 h-5" />
                )}
              </button>
              
              <button
                className="p-2 text-[var(--text-main)] hover:text-[var(--primary)] transition-colors duration-200"
                onClick={() => setMobileMenuOpen(true)}
                data-testid="mobile-menu-button"
              >
                <Menu className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        className={`overlay ${mobileMenuOpen ? 'visible' : ''}`}
        onClick={() => setMobileMenuOpen(false)}
      />

      {/* Mobile Menu */}
      <div className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`} data-testid="mobile-menu">
        <div className="p-6">
          <div className="flex items-center justify-between mb-10">
            <span className="font-mono text-xl font-bold text-[var(--primary)]">Menu</span>
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="p-2 text-[var(--text-main)] hover:text-[var(--primary)] transition-colors duration-200"
              data-testid="mobile-menu-close"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="flex flex-col gap-6">
            {navLinks.map((link) =>
              link.external ? (
                <a
                  key={link.href}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono text-lg text-[var(--text-main)] hover:text-[var(--primary)] transition-colors duration-200"
                  data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                >
                  {'>'} {link.name}
                </a>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`font-mono text-lg ${
                    isActive(link.path) ? 'text-[var(--primary)]' : 'text-[var(--text-main)]'
                  } hover:text-[var(--primary)] transition-colors duration-200`}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                >
                  {'>'} {link.name}
                </Link>
              )
            )}
            <Link
              to="/contact"
              className="mt-6 bg-[var(--primary)] text-black font-mono font-bold px-6 py-3 text-center uppercase tracking-widest"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-cta"
            >
              Get Started
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
